import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Label } from './ui/label';
import { 
  Users, 
  Plus, 
  Search, 
  Filter, 
  DollarSign,
  Calendar,
  Building2,
  TrendingUp,
  FileText,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Edit,
  Phone,
  Mail,
  MapPin,
  Loader,
  Target,
  BarChart3
} from 'lucide-react';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useViewState } from '../utils/viewState';

const CRM = ({ selectedLocation = 'All Locations' }) => {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [locations, setLocations] = useState([]);
  const [offices, setOffices] = useState([]);
  const [pipelineStats, setPipelineStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Persistent view state
  const [viewState, setViewState] = useViewState('crmDeals', { 
    view: 'pipeline' // pipeline, list, stats
  });
  const { view } = viewState;
  const [stageFilter, setStageFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddDeal, setShowAddDeal] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [viewingDeal, setViewingDeal] = useState(null);
  
  const { user } = useAuth();

  // Deal form state
  const [newDeal, setNewDeal] = useState({
    deal_name: '',
    company_name: '',
    contact: {
      name: '',
      email: '',
      phone: '',
      title: ''
    },
    agreement_type: 'new_business',
    membership_type: 'Private Office',
    location_id: '',
    office_ids: [],
    start_date: '',
    term_months: '',
    term_days: '',
    offered_price: '',
    probability: 50,
    notice_by: '',
    notes: '',
    source: '',
    assigned_to: user?.name || ''
  });

  // Load data on component mount and when location changes
  useEffect(() => {
    loadData();
  }, [selectedLocation]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {};
      if (selectedLocation !== 'All Locations') {
        filters.location = selectedLocation;
      }

      const [dealsData, locationsData, officesData, statsData] = await Promise.all([
        apiService.getDeals(filters),
        apiService.getLocations(),
        apiService.getOffices(filters),
        apiService.getPipelineStats(selectedLocation !== 'All Locations' ? selectedLocation : null)
      ]);

      setDeals(dealsData);
      setLocations(locationsData);
      setOffices(officesData);
      setPipelineStats(statsData);
      
      // Set default location for new deal
      if (selectedLocation !== 'All Locations') {
        const location = locationsData.find(loc => loc.name === selectedLocation);
        if (location) {
          setNewDeal(prev => ({ ...prev, location_id: location.id }));
        }
      }
    } catch (error) {
      console.error('Error loading CRM data:', error);
      setError('Failed to load CRM data');
    } finally {
      setLoading(false);
    }
  };

  // Add new deal
  const handleAddDeal = async () => {
    if (!newDeal.deal_name || !newDeal.company_name || !newDeal.contact.name || !newDeal.contact.email || !newDeal.location_id || !newDeal.offered_price) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      await apiService.createDeal({
        ...newDeal,
        offered_price: parseFloat(newDeal.offered_price),
        term_months: newDeal.term_months ? parseInt(newDeal.term_months) : null,
        term_days: newDeal.term_days ? parseInt(newDeal.term_days) : null,
        probability: parseInt(newDeal.probability)
      });

      setNewDeal({
        deal_name: '',
        company_name: '',
        contact: { name: '', email: '', phone: '', title: '' },
        agreement_type: 'new_business',
        membership_type: 'Private Office',
        location_id: selectedLocation !== 'All Locations' ? newDeal.location_id : '',
        office_ids: [],
        start_date: '',
        term_months: '',
        term_days: '',
        offered_price: '',
        probability: 50,
        notice_by: '',
        notes: '',
        source: '',
        assigned_to: user?.name || ''
      });
      setShowAddDeal(false);
      await loadData();
    } catch (error) {
      console.error('Error adding deal:', error);
      setError('Failed to add deal');
    }
  };

  // Update deal stage
  const handleStageChange = async (dealId, newStage) => {
    try {
      await apiService.updateDealStage(dealId, newStage);
      await loadData(); // Reload to get updated data
    } catch (error) {
      console.error('Error updating deal stage:', error);
      setError('Failed to update deal stage');
    }
  };

  // Generate agreement
  const handleGenerateAgreement = async (dealId) => {
    try {
      const result = await apiService.generateAgreement(dealId);
      if (result.success) {
        setError(null);
        // Show success message
        const successMessage = `Agreement generated successfully! URL: ${result.agreement_url}`;
        console.log(successMessage);
        // You could show a toast notification here
      }
      await loadData();
    } catch (error) {
      console.error('Error generating agreement:', error);
      setError('Failed to generate agreement');
    }
  };

  // Send for signature
  const handleSendForSignature = async (dealId) => {
    try {
      await apiService.sendForSignature(dealId);
      await loadData();
    } catch (error) {
      console.error('Error sending for signature:', error);
      setError('Failed to send for signature');
    }
  };

  // Filter deals
  const filteredDeals = deals.filter(deal => {
    const matchesStage = stageFilter === 'all' || deal.stage === stageFilter;
    const matchesSearch = 
      deal.deal_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      deal.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      deal.contact.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStage && matchesSearch;
  });

  // Group deals by stage for pipeline view
  const dealsByStage = {
    form_submitted: filteredDeals.filter(d => d.stage === 'form_submitted'),
    tour_booked: filteredDeals.filter(d => d.stage === 'tour_booked'),
    tour_completed: filteredDeals.filter(d => d.stage === 'tour_completed'),
    agreement_sent: filteredDeals.filter(d => d.stage === 'agreement_sent'),
    agreement_signed: filteredDeals.filter(d => d.stage === 'agreement_signed'),
    close_lost: filteredDeals.filter(d => d.stage === 'close_lost')
  };

  // Stage configurations
  const stageConfig = {
    form_submitted: { name: 'Form Submitted', color: 'bg-blue-100 text-blue-800 border-blue-300' },
    tour_booked: { name: 'Tour Booked', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
    tour_completed: { name: 'Tour Completed', color: 'bg-purple-100 text-purple-800 border-purple-300' },
    agreement_sent: { name: 'Agreement Sent', color: 'bg-orange-100 text-orange-800 border-orange-300' },
    agreement_signed: { name: 'Agreement Signed', color: 'bg-green-100 text-green-800 border-green-300' },
    close_lost: { name: 'Close Lost', color: 'bg-red-100 text-red-800 border-red-300' }
  };

  const getAvailableOffices = () => {
    return offices.filter(office => office.status === 'VACANT' || office.status === 'PENDING');
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-slate-600" />
            <p className="text-gray-600">Loading CRM data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center space-x-3">
            <Target className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Deals</h1>
              <p className="text-gray-600">Deal Pipeline & Sales Management</p>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Button
              variant={view === 'pipeline' ? 'default' : 'outline'}
              onClick={() => setViewState({ view: 'pipeline' })}
              size="sm"
            >
              Pipeline
            </Button>
            <Button
              variant={view === 'list' ? 'default' : 'outline'}
              onClick={() => setViewState({ view: 'list' })}
              size="sm"
            >
              List View
            </Button>
            <Button
              variant={view === 'stats' ? 'default' : 'outline'}
              onClick={() => setViewState({ view: 'stats' })}
              size="sm"
            >
              Statistics
            </Button>
          </div>
          <Button onClick={() => setShowAddDeal(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Add Deal
          </Button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
          {error}
          <Button 
            variant="ghost" 
            size="sm" 
            className="ml-2 text-red-600 hover:text-red-700"
            onClick={() => setError(null)}
          >
            ×
          </Button>
        </div>
      )}

      {/* Pipeline Stats Cards */}
      {pipelineStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card>
            <CardContent className="p-6 text-center">
              <Target className="w-8 h-8 mx-auto mb-2 text-blue-600" />
              <div className="text-2xl font-bold text-gray-900">{pipelineStats.total_deals}</div>
              <div className="text-sm text-gray-600">Total Deals</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 text-center">
              <DollarSign className="w-8 h-8 mx-auto mb-2 text-green-600" />
              <div className="text-2xl font-bold text-gray-900">
                ${pipelineStats.total_value?.toLocaleString() || '0'}
              </div>
              <div className="text-sm text-gray-600">Pipeline Value</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 text-center">
              <BarChart3 className="w-8 h-8 mx-auto mb-2 text-purple-600" />
              <div className="text-2xl font-bold text-gray-900">
                ${Math.round(pipelineStats.average_deal_value || 0).toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">Avg Deal Value</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 text-center">
              <TrendingUp className="w-8 h-8 mx-auto mb-2 text-amber-600" />
              <div className="text-2xl font-bold text-gray-900">{Math.round(pipelineStats.win_rate || 0)}%</div>
              <div className="text-sm text-gray-600">Win Rate</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center space-x-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search deals..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger className="w-48">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            <SelectItem value="form_submitted">Form Submitted</SelectItem>
            <SelectItem value="tour_booked">Tour Booked</SelectItem>
            <SelectItem value="tour_completed">Tour Completed</SelectItem>
            <SelectItem value="agreement_sent">Agreement Sent</SelectItem>
            <SelectItem value="agreement_signed">Agreement Signed</SelectItem>
            <SelectItem value="close_lost">Close Lost</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Content based on view */}
      {view === 'pipeline' && (
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-6 min-h-96">
          {Object.entries(dealsByStage).map(([stage, stageDeals]) => (
            <div key={stage} className="bg-white rounded-lg border p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">{stageConfig[stage].name}</h3>
                <Badge className={`${stageConfig[stage].color} border`}>
                  {stageDeals.length}
                </Badge>
              </div>
              <div className="space-y-3">
                {stageDeals.map((deal) => (
                  <Card key={deal.id} className="cursor-pointer hover:shadow-md transition-shadow mb-3">
                    <CardContent className="p-4" onClick={() => navigate(`/crm/deals/${deal.id}`)}>
                      <h4 className="font-medium text-gray-900 mb-2 text-sm leading-tight">{deal.deal_name}</h4>
                      <p className="text-sm text-gray-600 mb-2">{deal.company_name}</p>
                      <p className="text-sm font-semibold text-green-600 mb-2">
                        ${deal.offered_price?.toLocaleString()}
                      </p>
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                        <span className="truncate">{deal.contact.name}</span>
                        <span>{deal.probability}%</span>
                      </div>
                      <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                        {stage === 'tour_completed' && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="w-full text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleGenerateAgreement(deal.id);
                            }}
                          >
                            Generate Agreement
                          </Button>
                        )}
                        {stage === 'agreement_sent' && deal.signature_status === 'not_sent' && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="w-full text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSendForSignature(deal.id);
                            }}
                          >
                            Send for Signature
                          </Button>
                        )}
                        {deal.stage !== 'agreement_signed' && deal.stage !== 'close_lost' && (
                          <Select 
                            onValueChange={(value) => handleStageChange(deal.id, value)}
                            defaultValue={deal.stage}
                          >
                            <SelectTrigger className="h-7 text-xs w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(stageConfig).map(([key, config]) => (
                                <SelectItem key={key} value={key}>
                                  {config.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* List View */}
      {view === 'list' && (
        <Card>
          <CardContent className="p-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left p-4 text-sm font-medium text-gray-600">Deal</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">Company</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">Contact</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">Stage</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">Value</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">Probability</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDeals.map((deal) => (
                    <tr key={deal.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="p-4">
                        <div>
                          <div className="font-medium text-gray-900">{deal.deal_name}</div>
                          <div className="text-sm text-gray-500">{deal.location_name}</div>
                        </div>
                      </td>
                      <td className="p-4 font-medium text-gray-700">{deal.company_name}</td>
                      <td className="p-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{deal.contact.name}</div>
                          <div className="text-sm text-gray-500">{deal.contact.email}</div>
                        </div>
                      </td>
                      <td className="p-4">
                        <Badge className={`${stageConfig[deal.stage].color} border`}>
                          {stageConfig[deal.stage].name}
                        </Badge>
                      </td>
                      <td className="p-4 font-semibold text-green-700">
                        ${deal.offered_price?.toLocaleString()}
                      </td>
                      <td className="p-4">{deal.probability}%</td>
                      <td className="p-4">
                        <div className="flex items-center space-x-2">
                          <Button variant="outline" size="sm">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" size="sm">
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Statistics View */}
      {view === 'stats' && pipelineStats && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Pipeline Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(pipelineStats.stages).map(([stage, count]) => (
                  <div key={stage} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Badge className={`${stageConfig[stage].color} border`}>
                        {stageConfig[stage].name}
                      </Badge>
                    </div>
                    <div className="text-lg font-semibold text-gray-900">{count}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Performance Metrics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {Math.round(pipelineStats.win_rate || 0)}%
                  </div>
                  <div className="text-sm text-gray-600">Win Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">
                    ${Math.round(pipelineStats.average_deal_value || 0).toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-600">Average Deal Value</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600 mb-2">
                    ${pipelineStats.total_value?.toLocaleString() || '0'}
                  </div>
                  <div className="text-sm text-gray-600">Total Pipeline Value</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add Deal Dialog */}
      <Dialog open={showAddDeal} onOpenChange={setShowAddDeal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Deal</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">Basic Information</h3>
                <div>
                  <Label htmlFor="dealName">Deal Name *</Label>
                  <Input
                    id="dealName"
                    value={newDeal.deal_name}
                    onChange={(e) => setNewDeal({...newDeal, deal_name: e.target.value})}
                    placeholder="Q4 2024 - Acme Corp Office Lease"
                  />
                </div>
                <div>
                  <Label htmlFor="companyName">Company Name *</Label>
                  <Input
                    id="companyName"
                    value={newDeal.company_name}
                    onChange={(e) => setNewDeal({...newDeal, company_name: e.target.value})}
                    placeholder="Acme Corporation"
                  />
                </div>
                <div>
                  <Label htmlFor="agreementType">Agreement Type</Label>
                  <Select 
                    value={newDeal.agreement_type} 
                    onValueChange={(value) => setNewDeal({...newDeal, agreement_type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new_business">New Business</SelectItem>
                      <SelectItem value="renewal">Renewal</SelectItem>
                      <SelectItem value="expansion">Expansion</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="membershipType">Membership Type</Label>
                  <Select 
                    value={newDeal.membership_type} 
                    onValueChange={(value) => setNewDeal({...newDeal, membership_type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Private Office">Private Office</SelectItem>
                      <SelectItem value="Shared Space">Shared Space</SelectItem>
                      <SelectItem value="Meeting Rooms">Meeting Rooms</SelectItem>
                      <SelectItem value="Hot Desk">Hot Desk</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">Contact Information</h3>
                <div>
                  <Label htmlFor="contactName">Contact Name *</Label>
                  <Input
                    id="contactName"
                    value={newDeal.contact.name}
                    onChange={(e) => setNewDeal({
                      ...newDeal, 
                      contact: {...newDeal.contact, name: e.target.value}
                    })}
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <Label htmlFor="contactEmail">Contact Email *</Label>
                  <Input
                    id="contactEmail"
                    type="email"
                    value={newDeal.contact.email}
                    onChange={(e) => setNewDeal({
                      ...newDeal, 
                      contact: {...newDeal.contact, email: e.target.value}
                    })}
                    placeholder="john.smith@acme.com"
                  />
                </div>
                <div>
                  <Label htmlFor="contactPhone">Contact Phone</Label>
                  <Input
                    id="contactPhone"
                    value={newDeal.contact.phone}
                    onChange={(e) => setNewDeal({
                      ...newDeal, 
                      contact: {...newDeal.contact, phone: e.target.value}
                    })}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
                <div>
                  <Label htmlFor="contactTitle">Contact Title</Label>
                  <Input
                    id="contactTitle"
                    value={newDeal.contact.title}
                    onChange={(e) => setNewDeal({
                      ...newDeal, 
                      contact: {...newDeal.contact, title: e.target.value}
                    })}
                    placeholder="CEO"
                  />
                </div>
              </div>
            </div>

            {/* Location and Office Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">Location & Office Details</h3>
                <div>
                  <Label htmlFor="location">Bond Collective Location *</Label>
                  <Select 
                    value={newDeal.location_id} 
                    onValueChange={(value) => setNewDeal({...newDeal, location_id: value})}
                  >
                    <SelectTrigger>
                      <MapPin className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Select a location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((location) => (
                        <SelectItem key={location.id} value={location.id}>
                          {location.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="offices">Offices (Optional)</Label>
                  <Select 
                    onValueChange={(value) => {
                      if (!newDeal.office_ids.includes(value)) {
                        setNewDeal({...newDeal, office_ids: [...newDeal.office_ids, value]});
                      }
                    }}
                  >
                    <SelectTrigger>
                      <Building2 className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Select offices" />
                    </SelectTrigger>
                    <SelectContent>
                      {getAvailableOffices()
                        .filter(office => newDeal.location_id ? office.location_id === newDeal.location_id : true)
                        .map((office) => (
                        <SelectItem key={office.id} value={office.id}>
                          Office {office.office_number} - {office.desks} desks
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {newDeal.office_ids.length > 0 && (
                    <div className="space-y-3 mt-3">
                      <h4 className="text-sm font-medium text-gray-700">Selected Offices & Pricing</h4>
                      {newDeal.office_ids.map((officeId) => {
                        const office = offices.find(o => o.id === officeId);
                        return (
                          <Card key={officeId} className="p-3">
                            <div className="flex items-center justify-between mb-2">
                              <div className="font-medium">
                                Office {office?.office_number} - {office?.desks} desks
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-red-600"
                                onClick={() => setNewDeal({
                                  ...newDeal, 
                                  office_ids: newDeal.office_ids.filter(id => id !== officeId)
                                })}
                              >
                                ×
                              </Button>
                            </div>
                            {office?.marketed_rates && (
                              <div className="grid grid-cols-3 gap-2 text-xs">
                                <div className="text-center p-2 bg-blue-50 rounded">
                                  <div className="text-blue-600 font-medium">Monthly</div>
                                  <div className="font-bold text-blue-800">
                                    ${office.marketed_rates.monthly?.toLocaleString()}
                                  </div>
                                </div>
                                <div className="text-center p-2 bg-amber-50 rounded">
                                  <div className="text-amber-600 font-medium">6-Month</div>
                                  <div className="font-bold text-amber-800">
                                    ${office.marketed_rates.six_month?.toLocaleString()}
                                  </div>
                                </div>
                                <div className="text-center p-2 bg-emerald-50 rounded">
                                  <div className="text-emerald-600 font-medium">12-Month</div>
                                  <div className="font-bold text-emerald-800">
                                    ${office.marketed_rates.twelve_month?.toLocaleString()}
                                  </div>
                                </div>
                              </div>
                            )}
                          </Card>
                        );
                      })}
                      {newDeal.office_ids.length > 1 && (
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <div className="text-sm font-medium text-gray-700 mb-2">Combined Market Rate (Average)</div>
                          <div className="text-lg font-bold text-green-600">
                            ${Math.round(newDeal.office_ids.reduce((total, officeId) => {
                              const office = offices.find(o => o.id === officeId);
                              return total + (office?.marketed_rates?.monthly || 0);
                            }, 0) / newDeal.office_ids.length).toLocaleString()}
                            <span className="text-sm text-gray-500"> /month</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Deal Terms */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">Deal Terms</h3>
                <div>
                  <Label htmlFor="startDate">Start Date</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={newDeal.start_date}
                    onChange={(e) => setNewDeal({...newDeal, start_date: e.target.value})}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="termMonths">Term (Months)</Label>
                    <Input
                      id="termMonths"
                      type="number"
                      value={newDeal.term_months}
                      onChange={(e) => setNewDeal({...newDeal, term_months: e.target.value})}
                      placeholder="12"
                    />
                  </div>
                  <div>
                    <Label htmlFor="termDays">Term (Days)</Label>
                    <Input
                      id="termDays"
                      type="number"
                      value={newDeal.term_days}
                      onChange={(e) => setNewDeal({...newDeal, term_days: e.target.value})}
                      placeholder="365"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="offeredPrice">Offered Price (Monthly) *</Label>
                  <Input
                    id="offeredPrice"
                    type="number"
                    value={newDeal.offered_price}
                    onChange={(e) => setNewDeal({...newDeal, offered_price: e.target.value})}
                    placeholder="3500"
                  />
                </div>
                <div>
                  <Label htmlFor="probability">Probability (%)</Label>
                  <Input
                    id="probability"
                    type="number"
                    min="0"
                    max="100"
                    value={newDeal.probability}
                    onChange={(e) => setNewDeal({...newDeal, probability: e.target.value})}
                    placeholder="50"
                  />
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900">Additional Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="noticeBy">Notice By Period</Label>
                  <Input
                    id="noticeBy"
                    value={newDeal.notice_by}
                    onChange={(e) => setNewDeal({...newDeal, notice_by: e.target.value})}
                    placeholder="30 days"
                  />
                </div>
                <div>
                  <Label htmlFor="source">Lead Source</Label>
                  <Input
                    id="source"
                    value={newDeal.source}
                    onChange={(e) => setNewDeal({...newDeal, source: e.target.value})}
                    placeholder="Website, Referral, Cold Call, etc."
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={newDeal.notes}
                  onChange={(e) => setNewDeal({...newDeal, notes: e.target.value})}
                  placeholder="Additional notes about this deal..."
                  rows={3}
                />
              </div>
            </div>

            <div className="flex space-x-2">
              <Button onClick={handleAddDeal} className="flex-1">
                Create Deal
              </Button>
              <Button variant="outline" onClick={() => setShowAddDeal(false)} className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Deal Detail View Dialog */}
      <Dialog open={!!viewingDeal} onOpenChange={() => setViewingDeal(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{viewingDeal?.deal_name}</span>
              <Badge className={`${stageConfig[viewingDeal?.stage]?.color} border`}>
                {stageConfig[viewingDeal?.stage]?.name}
              </Badge>
            </DialogTitle>
          </DialogHeader>
          {viewingDeal && (
            <div className="space-y-6">
              {/* Deal Overview */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-2">Deal Value</h3>
                    <div className="text-2xl font-bold text-green-600">
                      ${viewingDeal.offered_price?.toLocaleString()}
                      <span className="text-sm text-gray-500">/month</span>
                    </div>
                    {viewingDeal.deal_value && (
                      <div className="text-sm text-gray-600 mt-1">
                        Total: ${viewingDeal.deal_value.toLocaleString()}
                      </div>
                    )}
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-2">Probability</h3>
                    <div className="text-2xl font-bold text-blue-600">
                      {viewingDeal.probability}%
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-2">Market Rate</h3>
                    <div className="text-2xl font-bold text-purple-600">
                      {viewingDeal.market_rate ? `$${viewingDeal.market_rate.toLocaleString()}` : 'N/A'}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Company & Contact Info */}
                <Card>
                  <CardHeader>
                    <CardTitle>Company & Contact</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <div className="text-sm text-gray-600">Company</div>
                      <div className="font-semibold">{viewingDeal.company_name}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Primary Contact</div>
                      <div className="font-semibold">{viewingDeal.contact.name}</div>
                      {viewingDeal.contact.title && (
                        <div className="text-sm text-gray-500">{viewingDeal.contact.title}</div>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="text-sm">{viewingDeal.contact.email}</span>
                    </div>
                    {viewingDeal.contact.phone && (
                      <div className="flex items-center space-x-2">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <span className="text-sm">{viewingDeal.contact.phone}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Deal Details */}
                <Card>
                  <CardHeader>
                    <CardTitle>Deal Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <div className="text-sm text-gray-600">Agreement Type</div>
                      <div className="font-semibold capitalize">{viewingDeal.agreement_type.replace('_', ' ')}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Membership Type</div>
                      <div className="font-semibold">{viewingDeal.membership_type}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Location</div>
                      <div className="font-semibold">{viewingDeal.location_name}</div>
                    </div>
                    {viewingDeal.start_date && (
                      <div>
                        <div className="text-sm text-gray-600">Start Date</div>
                        <div className="font-semibold">{new Date(viewingDeal.start_date).toLocaleDateString()}</div>
                      </div>
                    )}
                    {viewingDeal.term_months && (
                      <div>
                        <div className="text-sm text-gray-600">Term</div>
                        <div className="font-semibold">{viewingDeal.term_months} months</div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Notes */}
              {viewingDeal.notes && (
                <Card>
                  <CardHeader>
                    <CardTitle>Notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700">{viewingDeal.notes}</p>
                  </CardContent>
                </Card>
              )}

              {/* Actions */}
              <div className="flex items-center space-x-4">
                {viewingDeal.stage === 'tour_completed' && (
                  <Button 
                    onClick={() => {
                      handleGenerateAgreement(viewingDeal.id);
                      setViewingDeal(null);
                    }}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Generate Agreement
                  </Button>
                )}
                {viewingDeal.stage === 'agreement_sent' && viewingDeal.signature_status === 'not_sent' && (
                  <Button 
                    onClick={() => {
                      handleSendForSignature(viewingDeal.id);
                      setViewingDeal(null);
                    }}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send for Signature
                  </Button>
                )}
                <Button 
                  variant="outline"
                  onClick={() => {
                    setEditingDeal(viewingDeal);
                    setViewingDeal(null);
                  }}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Deal
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CRM;